/**
 * User choices collected before taking a copy of SOMEONE ELSE'S trip (a Trip
 * Market listing, or a clone/template share code).
 *
 * A cross-user copy is the itinerary only — activities and accommodations. The
 * source owner's flights, car rentals, expenses, documents and collaborators
 * are never copied, so there is nothing to choose about them.
 *
 * Duplicating your OWN trip does not use this: those are your own bookings and
 * keep coming across untouched.
 *
 * Mirrors `lib/models/clone_options.dart` on mobile.
 */
export interface CloneOptions {
    /**
     * The day the copy should start on. Every date in the trip shifts by the
     * resulting whole-day offset, so the itinerary keeps its shape and length.
     * `null` means "keep the original trip's dates".
     */
    newStartDate: Date | null;
    /**
     * Whether the source owner's prices come along. Defaults to false —
     * another traveller's prices are stale and were never the cloner's.
     */
    keepPrices: boolean;
}

/** Local-midnight `YYYY-MM-DD`, the shape the clone callables expect. */
export function toIsoDay(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * The two fields understood by the `cloneMarketTrip` / `cloneSharedTrip`
 * callables. The server owns the offset calculation — it reads the source
 * trip's start date, which the caller may not be allowed to read yet — so only
 * the *desired* start date is sent. Omitting [options] yields the defaults
 * (original dates, no prices).
 */
export function cloneOptionsPayload(
    options?: CloneOptions,
): { newStartDate?: string; keepPrices: boolean } {
    if (!options) return { keepPrices: false };
    return {
        ...(options.newStartDate ? { newStartDate: toIsoDay(options.newStartDate) } : {}),
        keepPrices: options.keepPrices,
    };
}
